import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, TemplateRef, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { RentalObjectCategory } from '../../model/const/enums';
import { SelectionItem } from '../../../core/component/select/select.component';
import { LocalDate } from '../../../core/model/local-date';
import { TransferType, transferTypeToString } from '../../model/const/transfer-type';
import { Transfer } from '../../model/Transfer/transfer';
import { NavigationService } from '../../../core/service/navigation.service';
import { CooperativeHousingContractService } from '../../service/data/cooperative-housing-contract.service';
import { ActivatedRoute } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { GetInvoiceDeliveryMethodIcon, InvoiceDeliveryMethod, InvoiceDeliveryMethodToString } from '../../../invoice/model/const/enum';
import { Money } from '../../../core/model/money';
import { PostalAddress } from '../../../common/model/postal-address';
import { TenantService } from '../../service/data/tenant.service';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { TabOption } from '../../../core/component/tab-group/tab-option';
import { numberValue } from '../../../core/helper/number-value';

@Component({
    selector: 'app-brf-transfer-contract',
    templateUrl: './brf-transfer.component.html',
    styleUrls: ['./brf-transfer.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BrfTransferComponent implements OnDestroy, OnInit, AfterViewInit {
  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _fcf: FormControlExFactory,
    private readonly _service: CooperativeHousingContractService,
    private readonly _navService: NavigationService,
    private readonly _fb: FormBuilder,
    public readonly tenantService: TenantService
  ) {}

  private _subscription: Subscription = new Subscription();
  dataContext: Transfer;
  form: FormGroup;

  @ViewChildren('tab_simpleSell, tab_advancedSell') tabsTemplates: QueryList<TemplateRef<any>>;
  modeSelection: TabOption[] = [];

  ngAfterViewInit(): void {
    // Conditional rendering makes TemplateRef temporarily undefined. ViewChildren is used to listen for changes.

    this.tabsTemplates.changes.subscribe((templateList: QueryList<TemplateRef<any>>) => {
      const tabSimpleSell = templateList.first;
      const tabAdvancedSell = templateList.last;

      setTimeout(() => {
        this.modeSelection = [
          new TabOption('COOPERATIVE_HOUSING.TRANSFER_SIMPLE', 1, tabSimpleSell),
          new TabOption('COOPERATIVE_HOUSING.TRANSFER_ADVANCED', 2, tabAdvancedSell),
        ];
      });
    });
  }

  get advancedSell(): boolean {
    return this.isDivisionOfProperty || (this.form && this.form.controls['mode'].value === 2);
  }

  get showSellAmount(): boolean {
    const type = this.form.controls['transferType'].value;
    return type === TransferType.Purchase || type === TransferType.ForcedSale;
  }

  get showTabbedButtons(): boolean {
    return this.sellers.length > 1 && !this.isDivisionOfProperty;
  }

  IsSellAmountMandatory(val: any): boolean {
    return val === TransferType.Purchase || val === TransferType.ForcedSale;
  }

  transferTypes: SelectionItem[] = [
    new SelectionItem(transferTypeToString(TransferType.Purchase), TransferType.Purchase),
    new SelectionItem(transferTypeToString(TransferType.PartitionOfProperty), TransferType.PartitionOfProperty),
    new SelectionItem(transferTypeToString(TransferType.Gift), TransferType.Gift),
    new SelectionItem(transferTypeToString(TransferType.Inheritance), TransferType.Inheritance),
    new SelectionItem(transferTypeToString(TransferType.ForcedSale), TransferType.ForcedSale),
  ];

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }
  }

  ngOnInit() {
    this._subscription.add(
      this._activatedRoute.paramMap
        .pipe(
          switchMap(map => {
            let id = map.get('leaseAgreementId');
            return this._service.getTransferDefaults(id);
          })
        )
        .subscribe(res => {
          this.dataContext = res;
          this.form = this.createForm();
          this.addBuyer();
        })
    );
  }

  invoiceDeliveryMethods = [
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Mail), value: InvoiceDeliveryMethod.Mail, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Mail) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Manual), value: InvoiceDeliveryMethod.Manual, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Manual) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithPDF), value: InvoiceDeliveryMethod.EmailWithPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithPDF) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithoutPDF), value: InvoiceDeliveryMethod.EmailWithoutPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithoutPDF) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EInvoiceB2B), value: InvoiceDeliveryMethod.EInvoiceB2B, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EInvoiceB2B) },
  ];

  private sumMoneyZeroIsNull(values: Money[]): Money {
    let amount = values
      .filter(v => v && v.amount)
      .map(v => numberValue(v.amount))
      .reduce((a, b) => a + b, 0);
    return new Money(this.dataContext.currencyCode, amount ? amount : null);
  }

  createForm(): FormGroup {
    this.buyers = this._fb.array([]);
    this.sellerBuyAmountDefaults = this.dataContext.sellers.map(seller => ({
      id: seller.contactId,
      amount: seller.buyAmount_643 ? seller.buyAmount_643.amount : null
    }));

    let form = this._fb.group({
      oldAgreementId: this._fcf.createHidden(this.dataContext.oldAgreementId),
      oldLeaseAgreementNumber: this._fcf.createReadonly('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', this.dataContext.oldLeaseAgreementNumber),
      rentalId: this._fcf.createReadonly('RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL', this.dataContext.rentalId),
      mode: this._fcf.createHidden(1),
      sellers: this.createSellerForms(),
      buyers: this.buyers,
      agreementDate: this._fcf.createMandatory('LEASE_AGREEMENT.AGREEMENT_DATE', this.dataContext.agreementDate),
      emailInvoicing: this._fcf.create('COMMON.EMAIL_INVOICING', null),
      fromDate: this._fcf.createMandatory('COOPERATIVE_HOUSING.ACCESS_DATE', this.dataContext.fromDate),
      invoiceDeliveryMethod: this._fcf.createMandatory('LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD', this.dataContext.invoiceDeliveryMethod),
      innerFund_635: this._fcf.createMandatory('COOPERATIVE_HOUSING.KU55.INNER_FUND_635', new Money(this.dataContext.currencyCode, null)),
      //invoicePayment: number,
      //rentPayment: number,
      additionalPayment_639: this._fcf.createMandatory(
        'COOPERATIVE_HOUSING.KU55.ADDITIONAL_TRANSFER_AMOUNT_639',
        new Money(this.dataContext.currencyCode, null)
      ),
      leaseAgreementNumber: this._fcf.createMandatory('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', this.dataContext.leaseAgreementNumber),
      payerContactId: this._fcf.createWithOptions('LEASE_AGREEMENT.PAYER', null, {
        required: true,
        helpText: 'LEASE_AGREEMENT.HELP_TEXT.INVOICE_PAYER_BRF',
      }),
      individualInvoiceAddress: this._fcf.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', false),
      invoiceAddress: this._fb.group({}),
      purchaseAmount: this._fcf.createMandatory('COOPERATIVE_HOUSING.TRANSFER_AMOUNT', new Money(this.dataContext.currencyCode, null)),
      transferType: this._fcf.createMandatory('COOPERATIVE_HOUSING.TRANSFER_TYPE', this.dataContext.transferType),
      objectCategory: this._fcf.createHidden(this.dataContext.objectCategory),
    });
    this._subscription.add(
      form.controls['fromDate'].valueChanges.subscribe(val => {
        let fromDateTime = this.fromDate ? this.fromDate.toDate().getTime() : null;
        if (val) {
          for (let ownerForm of this.buyers.controls.map(c => c as FormGroup)) {
            if (
              !ownerForm.controls['memberFrom'].value ||
              LocalDate.fromDate(ownerForm.controls['memberFrom'].value).toDate().getTime() === fromDateTime
            ) {
              ownerForm.controls['memberFrom'].setValue(val);
            }
          }
          this.fromDate = val;
        }
      })
    );

    this._subscription.add(
      form.controls['transferType'].valueChanges.subscribe(val => {
        if (val) {
          (form.controls['additionalPayment_639'] as FormControlEx).required = this.IsSellAmountMandatory(val);
          form.controls['additionalPayment_639'].updateValueAndValidity();
          (form.controls['purchaseAmount'] as FormControlEx).required = this.IsSellAmountMandatory(val);
          form.controls['purchaseAmount'].updateValueAndValidity();
        }
      })
    );

    return form;
  }

  private fromDate: LocalDate;

  emptyAddressModel: PostalAddress;
  buyers: FormArray;
  sellers: FormArray;  
  sellerAddresses: PostalAddress[] = [];
  sellerBuyAmountDefaults: { id: string, amount: number }[];

  createSellerForms(): FormArray {
    this.sellers = this._fb.array([]);
    for (let seller of this.dataContext.sellers) {
      let form = this._fb.group({
        contactId: this._fcf.createHidden(seller.contactId),
        ownedShare: this._fcf.createReadonly('COOPERATIVE_HOUSING.OWNED_SHARE', seller.ownedShare),
        soldShare: this._fcf.createWithOptions('COOPERATIVE_HOUSING.TRANSFERRED_SHARE', seller.soldShare, {
          required: true,
          validator: Validators.compose([Validators.min(0), c => this._validateShareSum(c)]),
        }),
        name: this._fcf.createReadonly('COOPERATIVE_HOUSING.OWNER', seller.name),
        buyDate_640: this._fcf.createMandatory('COOPERATIVE_HOUSING.KU55.BUY_DATE_640', seller.buyDate_640),
        buyAmount_643: this._fcf.createMandatory(
          'COOPERATIVE_HOUSING.KU55.BUY_AMOUNT_643',
          new Money(this.dataContext.currencyCode, seller.buyAmount_643 ? seller.buyAmount_643.amount : null)
        ),
        postalAddress: this._fb.group({}),
        isBuyer: this._fcf.createHidden(false),
        memberTo: this._fcf.create('COOPERATIVE_HOUSING.MEMBER_TO', null, Validators.required),
        memberFrom: this._fcf.createHidden(LocalDate.fromDate(seller.memberFrom)),
        membershipApproved: this._fcf.createHidden(LocalDate.fromDate(seller.membershipApproved)),
        endMembership: this._fcf.create('COOPERATIVE_HOUSING.END_MEMBERSHIP', true),
        acquisitionGiftOrInheritance_642: this._fcf.createHidden(seller.acquisitionGiftOrInheritance_642),
      });
      this._subscription.add(
        form.controls['endMembership'].valueChanges.subscribe(val => {
          let validator = val ? Validators.required : null;
          form.controls['memberTo'].setValidators(validator);
        })
      );
      this._subscription.add(
        form.controls['isBuyer'].valueChanges.subscribe(val => {
          if (val) {
            form.controls['endMembership'].setValue(false);
          }
        })
      );
      this._subscription.add(
        form.controls['soldShare'].valueChanges.subscribe(val => {
          let sellerDefault = this.sellerBuyAmountDefaults.find(x => x.id === form.controls['contactId'].value);
          if (sellerDefault && sellerDefault.amount != null && sellerDefault.amount !== 0)
            form.controls['buyAmount_643'].setValue(new Money(this.dataContext.currencyCode, val / form.controls['ownedShare'].value * sellerDefault.amount));

          for (let f of this.buyers.controls.map(c => c as FormGroup)) {
            this.updateValidity(f);
          }
          for (let s of this.sellers.controls.map(c => c as FormGroup)) {
            this.updateValidity(s);
          }
        })
      );
      
      this.sellers.push(form);
      let address = new PostalAddress();
      address.country = this.dataContext.invoiceAddress.country;
      this.sellerAddresses.push(address);
    }
    return this.sellers;
  }

  updateValidity(group: FormGroup): void {
    for (const field in group.controls) {
      const abstractControl = group.controls[field];
      if (abstractControl instanceof AbstractControl) {
        abstractControl.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  get isDwelling(): boolean {
    return this.form && this.form.controls['objectCategory'].value === RentalObjectCategory.Dwelling;
  }

  addBuyer() {
    let soldShare = this.sellers.controls
      .map(c => numberValue((c as FormGroup).controls['soldShare'].value))
      .filter(share => share)
      .reduce((a, b) => a + b, 0);
    let currentShare = this.buyers.controls
      .map(c => numberValue((c as FormGroup).controls['acquiredShare'].value))
      .filter(share => share)
      .reduce((a, b) => a + b, 0);

    let form = this._fb.group({
      changeAddress: this._fcf.create('COOPERATIVE_HOUSING.TRANSFER_CHANGE_BUYER_ADDRESS', true),
      contactId: this._fcf.createMandatory('COOPERATIVE_HOUSING.OWNER', null),
      memberFrom: this._fcf.createMandatory('COOPERATIVE_HOUSING.MEMBER_FROM', this.form.controls['fromDate'].value),
      approvalDate: this._fcf.create('COOPERATIVE_HOUSING.MEMBER_APPROVAL_DATE', null),
      acquiredShare: this._fcf.createWithOptions(
        'COOPERATIVE_HOUSING.BOUGHT_SHARE',
        currentShare > soldShare ? null : soldShare - currentShare,
        { required: true, validator: Validators.compose([Validators.min(0.01), c => this._validateShareSum(c)]) }
      ),
    });
    this._subscription.add(
      form.controls['acquiredShare'].valueChanges.subscribe(val => {
        for (let f of this.sellers.controls.map(c => c as FormGroup)) {
          this.updateValidity(f);
        }
        for (let b of this.buyers.controls.map(c => c as FormGroup)) {
          this.updateValidity(b);
        }
      })
    );
    this.buyers.push(form);
  }

  removeBuyer(index: number) {
    this.buyers.removeAt(index);
    this.updateBuyersSoldShare();
  }

  private updateBuyersSoldShare() {
    let buyerIds = this.buyers.controls.map(c => (c as FormGroup).controls['contactId'].value).filter(id => id);
    for (let sellerForm of this.sellers.controls.map(c => c as FormGroup)) {
      let isBuyer = buyerIds.find(id => sellerForm.controls['contactId'].value == id) ? true : false;
      if (isBuyer) {
        sellerForm.controls['soldShare'].setValue(0);
      }
      sellerForm.controls['isBuyer'].setValue(isBuyer);
    }
  }

  get isDivisionOfProperty(): boolean {
    return this.form && this.form.controls['transferType'].value == TransferType.PartitionOfProperty;
  }

  private updateDefaultBoughtShare() {
    let buyerForms = this.buyers.controls.map(c => c as FormGroup);
    if (buyerForms.length > 1) {
      return;
    }
    let boughtShare = 0;
    for (let sellerForm of this.sellers.controls.map(c => c as FormGroup)) {
      let isBuyer = sellerForm.controls['isBuyer'].value;
      if (!isBuyer) {
        boughtShare += numberValue(sellerForm.controls['soldShare'].value);
      }
    }
    buyerForms[0].controls['acquiredShare'].setValue(boughtShare);
  }

  buyerSelected(item: AutoCompleteSelectionItem, formIndex: number) {
    if (formIndex === 0) {
      this.form.controls['payerContactId'].setValue(item.value);      
      (this.form.get('invoiceAddress') as FormGroup).patchValue({
        name: item.data.name,        
      })
    }
    this.updateBuyersSoldShare();
    this.updateDefaultBoughtShare();
    this.updateBuyerFields(item.value, formIndex);
  }

  updateBuyerFields(contactId: string, formIndex: number) {
    let form = this.buyers.controls.map(c => c as FormGroup)[formIndex];
    let sellerForm = this.sellers.controls.map(c => c as FormGroup).find(f => f.controls['contactId'].value == contactId);
    if (sellerForm) {
      form.controls['memberFrom'].setValue(sellerForm.controls['memberFrom'].value);
      form.controls['approvalDate'].setValue(sellerForm.controls['membershipApproved'].value);

      let resetFields = ['buyAmount_643'];
      for (let field of resetFields) {
        sellerForm.controls[field].setValue(new Money(this.dataContext.currencyCode, 0));
      }
    }
  }

  private _validateShareSum(control: AbstractControl): ValidationErrors {
    let soldShare = this.sellers.controls
      .map(c => numberValue((c as FormGroup).controls['soldShare'].value))
      .filter(share => share)
      .reduce((a, b) => a + b, 0);
    let boughtShare = this.buyers.controls
      .map(c => numberValue((c as FormGroup).controls['acquiredShare'].value))
      .filter(share => share)
      .reduce((a, b) => a + b, 0);

    if (boughtShare != soldShare) {
      return { function: { message: 'COOPERATIVE_HOUSING.SOLD_AND_BOUGHT_SUM_ERROR', params: {} } };
    }
    return null;
  }

  cancel() {
    this._navService.navigateBack();
  }

  onSubmit() {
    this.updateValidity(this.form);    

    for (let f of this.sellers.controls.map(c => c as FormGroup)) {
      this.updateValidity(f);
    }
    for (let f of this.buyers.controls.map(c => c as FormGroup)) {
      this.updateValidity(f);
    }    
    this.form.markAllAsTouched();    

    if (this.form.valid) {
      let context = this.form.value;
      this._subscription.add(
        this._service.createTransfer(context).subscribe(res => {          
          this._navService.navigate(['co-housing', 'contracts', 'edit', res], null, false);
        })
      );
    }
  }
}
